import { useState } from 'react';

import { LocaleLink as Link } from '../lib/locale-link.js';
import { useMarketingSeo } from '../lib/use-marketing-seo.js';
import { MarketingShell } from '../components/marketing-shell.js';
import { useTrialDays } from '../lib/use-trial-days.js';
import { useI18n } from '../lib/use-i18n.js';
import { RevealBlock } from '../components/reveal-block.js';
import { usePlatformBrand } from '../lib/use-platform-brand.js';
import { defaultPlans, defaultPlanId } from '@kilat/config-seed';
import { money } from '@kilat/shared';

/**
 * /pricing — one plan today (Starter), with extra-outlet add-on math
 * + an interactive "what would 3 outlets cost?" slider.
 *
 * The numbers come from `@kilat/config-seed/plans.ts` so any future
 * change there (super-admin re-prices the plan via /superadmin/plans
 * Phase-2) lands here automatically.
 */
export function PricingPage() {
  const brand = usePlatformBrand();
  const { t } = useI18n();
  const trialDays = useTrialDays();
  useMarketingSeo({
    title: t('marketing.pricing.metaTitle', { brand: brand.name }),
    description: t('marketing.pricing.metaDescription', { brand: brand.name, days: trialDays }),
  });
  const plan = defaultPlans.find((p) => p.id === defaultPlanId) ?? defaultPlans[0];
  if (!plan) {
    return (
      <MarketingShell>
        <section style={{ padding: 'var(--space-12) var(--space-6)' }}>
          <p>{t('marketing.pricing.noData')}</p>
        </section>
      </MarketingShell>
    );
  }
  return (
    <MarketingShell>
      <Hero />
      <PlanCard plan={plan} />
      <Calculator plan={plan} />
      <FaqSection />
      <BottomCta />
    </MarketingShell>
  );
}

function Hero() {
  const { t } = useI18n();
  return (
    <section
      style={{
        position: 'relative',
        // Same ambient wash as /features and the home hero — one site, one
        // visual system, at an amplitude that suits a secondary page.
        background:
          'radial-gradient(120% 90% at 50% -10%, rgba(var(--color-primary-rgb), 0.10), transparent 62%)',
        padding: 'var(--space-12) var(--space-6) var(--space-6) var(--space-6)',
        textAlign: 'center',
      }}
    >
      <RevealBlock style={{ maxWidth: 720, margin: '0 auto' }}>
        <p style={eyebrowStyle}>{t('marketing.pricing.eyebrow')}</p>
        <h1
          style={{
            fontSize: 'var(--text-display)',
            lineHeight: 1.05,
            fontWeight: 700,
            margin: 'var(--space-2) 0 0 0',
            letterSpacing: '-0.02em',
          }}
        >
          {t('marketing.pricing.heroTitle')}
        </h1>
        <p
          style={{
            margin: 'var(--space-4) auto 0 auto',
            maxWidth: 540,
            fontSize: 'var(--text-body)',
            color: 'var(--color-on-surface-muted)',
            lineHeight: 1.5,
          }}
        >
          {t('marketing.pricing.heroSubtitle')}
        </p>
      </RevealBlock>
    </section>
  );
}

function PlanCard({
  plan,
}: {
  readonly plan: {
    id: string;
    name: string;
    basePrice: number;
    extraOutletPrice: number;
    currency: string;
  };
}) {
  const { t } = useI18n();
  const trialDays = useTrialDays();
  const includes = [
    t('marketing.pricing.include1'),
    t('marketing.pricing.include2'),
    t('marketing.pricing.include3'),
    t('marketing.pricing.include4'),
    t('marketing.pricing.include5'),
    t('marketing.pricing.include6'),
    t('marketing.pricing.include7'),
    t('marketing.pricing.include8'),
    t('marketing.pricing.include9'),
    t('marketing.pricing.include10'),
  ];
  return (
    <section style={{ padding: 'var(--space-6) var(--space-6) var(--space-12) var(--space-6)' }}>
      <div
        style={{
          maxWidth: 480,
          margin: '0 auto',
          padding: 'var(--space-8)',
          borderRadius: 'var(--radius-lg)',
          background: 'var(--color-surface)',
          border: '2px solid var(--color-primary)',
          boxShadow: 'var(--shadow-2)',
        }}
      >
        <p
          style={{
            margin: 0,
            fontSize: 'var(--text-meta)',
            letterSpacing: 4,
            textTransform: 'uppercase',
            color: 'var(--color-primary)',
            fontWeight: 600,
          }}
        >
          {plan.name}
        </p>
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            marginTop: 'var(--space-3)',
            gap: 'var(--space-2)',
          }}
        >
          <span
            style={{
              fontSize: 'var(--text-display)',
              fontWeight: 700,
              letterSpacing: '-0.02em',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {money.format(plan.basePrice)}
          </span>
          <span style={{ color: 'var(--color-on-surface-muted)', fontSize: 'var(--text-body)' }}>
            {t('marketing.pricing.perMonth')}
          </span>
        </div>
        <p
          style={{
            margin: 'var(--space-2) 0 0 0',
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
          }}
        >
          {t('marketing.pricing.planNote', { price: money.format(plan.extraOutletPrice) })}
        </p>

        <ul
          style={{
            listStyle: 'none',
            margin: 'var(--space-6) 0 0 0',
            padding: 0,
            display: 'grid',
            gap: 'var(--space-2)',
            fontSize: 'var(--text-meta)',
          }}
        >
          {includes.map((line) => (
            <li
              key={line}
              style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'baseline' }}
            >
              <span style={{ color: 'var(--color-primary)', fontWeight: 700 }} aria-hidden>
                {'✓'}
              </span>
              {line}
            </li>
          ))}
        </ul>

        <Link
          to="/signup"
          style={{
            display: 'block',
            marginTop: 'var(--space-6)',
            padding: '14px 24px',
            borderRadius: 'var(--radius-pill)',
            background: 'var(--color-primary)',
            color: 'var(--color-primary-fg)',
            fontWeight: 600,
            textDecoration: 'none',
            textAlign: 'center',
            boxShadow: 'var(--shadow-2)',
          }}
        >
          {t('marketing.pricing.planCta', { days: trialDays })}
        </Link>
        <p
          style={{
            margin: 'var(--space-3) 0 0 0',
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
            textAlign: 'center',
          }}
        >
          {t('marketing.pricing.planCardNote')}
        </p>
      </div>
    </section>
  );
}

function Calculator({
  plan,
}: {
  readonly plan: { basePrice: number; extraOutletPrice: number; currency: string };
}) {
  const { t } = useI18n();
  const [outlets, setOutlets] = useState(1);
  const extras = Math.max(0, outlets - 1);
  const total = plan.basePrice + extras * plan.extraOutletPrice;
  return (
    <section
      style={{ padding: 'var(--space-12) var(--space-6)', background: 'var(--tint-subtle)' }}
    >
      <div style={{ maxWidth: 720, margin: '0 auto', textAlign: 'center' }}>
        <p style={eyebrowStyle}>{t('marketing.pricing.calcEyebrow')}</p>
        <h2
          style={{
            margin: 'var(--space-2) 0 0 0',
            fontSize: 'var(--text-h1)',
            fontWeight: 600,
            letterSpacing: '-0.01em',
          }}
        >
          {t('marketing.pricing.calcTitle')}
        </h2>
        <div
          style={{
            marginTop: 'var(--space-8)',
            padding: 'var(--space-6)',
            borderRadius: 'var(--radius-lg)',
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            boxShadow: 'var(--shadow-1)',
          }}
        >
          <label style={{ display: 'block', textAlign: 'left' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                marginBottom: 'var(--space-2)',
              }}
            >
              <span
                style={{
                  fontSize: 'var(--text-meta)',
                  color: 'var(--color-on-surface-muted)',
                  fontWeight: 500,
                }}
              >
                {t('marketing.pricing.calcOutletsLabel')}
              </span>
              <span
                style={{
                  fontSize: 'var(--text-h2)',
                  fontWeight: 700,
                  color: 'var(--color-primary)',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {outlets}
              </span>
            </div>
            <input
              type="range"
              min={1}
              max={10}
              step={1}
              value={outlets}
              onChange={(e) => setOutlets(Number(e.target.value))}
              style={{ width: '100%', accentColor: 'var(--color-primary)' }}
            />
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: 'var(--text-meta)',
                color: 'var(--color-on-surface-muted)',
                marginTop: 'var(--space-1)',
              }}
            >
              <span>1</span>
              <span>10</span>
            </div>
          </label>
          <div
            style={{
              marginTop: 'var(--space-6)',
              paddingTop: 'var(--space-4)',
              borderTop: '1px solid var(--color-border)',
              display: 'grid',
              gap: 'var(--space-2)',
              fontSize: 'var(--text-meta)',
              textAlign: 'left',
            }}
          >
            <Row label={t('marketing.pricing.calcRowBase')} value={money.format(plan.basePrice)} />
            {extras > 0 ? (
              <Row
                label={t('marketing.pricing.calcRowExtra', {
                  count: extras,
                  price: money.format(plan.extraOutletPrice),
                })}
                value={money.format(extras * plan.extraOutletPrice)}
              />
            ) : null}
            <Row label={t('marketing.pricing.calcRowTotal')} value={money.format(total)} emphasis />
          </div>
        </div>
        <p
          style={{
            margin: 'var(--space-6) 0 0 0',
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
          }}
        >
          {t('marketing.pricing.calcNote')}
        </p>
      </div>
    </section>
  );
}

function Row({
  label,
  value,
  emphasis = false,
}: {
  readonly label: string;
  readonly value: string;
  readonly emphasis?: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: 'var(--space-3)',
        fontWeight: emphasis ? 700 : 400,
        fontSize: emphasis ? 'var(--text-body)' : 'var(--text-meta)',
        color: emphasis ? 'var(--color-on-surface)' : 'var(--color-on-surface-muted)',
      }}
    >
      <span>{label}</span>
      <span style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</span>
    </div>
  );
}

interface Faq {
  readonly q: string;
  readonly a: string;
}

function FaqSection() {
  const { t } = useI18n();
  const trialDays = useTrialDays();
  const faqs: readonly Faq[] = [
    { q: t('marketing.pricing.faq1Q'), a: t('marketing.pricing.faq1A', { days: trialDays }) },
    { q: t('marketing.pricing.faq2Q'), a: t('marketing.pricing.faq2A') },
    { q: t('marketing.pricing.faq3Q'), a: t('marketing.pricing.faq3A') },
    { q: t('marketing.pricing.faq4Q'), a: t('marketing.pricing.faq4A') },
    { q: t('marketing.pricing.faq5Q'), a: t('marketing.pricing.faq5A') },
  ];
  return (
    <section style={{ padding: 'var(--space-12) var(--space-6)' }}>
      <RevealBlock style={{ maxWidth: 720, margin: '0 auto' }}>
        <p style={{ ...eyebrowStyle, textAlign: 'center' }}>{t('marketing.pricing.faqEyebrow')}</p>
        <h2
          style={{
            margin: 'var(--space-2) 0 0 0',
            fontSize: 'var(--text-h1)',
            fontWeight: 600,
            textAlign: 'center',
            letterSpacing: '-0.01em',
          }}
        >
          {t('marketing.pricing.faqTitle')}
        </h2>
        <div style={{ marginTop: 'var(--space-8)', display: 'grid', gap: 'var(--space-3)' }}>
          {faqs.map((item) => (
            <details
              key={item.q}
              style={{
                padding: 'var(--space-4) var(--space-5)',
                borderRadius: 'var(--radius-md)',
                background: 'var(--color-surface)',
                border: '1px solid var(--color-border)',
              }}
            >
              <summary
                style={{
                  cursor: 'pointer',
                  listStyle: 'none',
                  fontSize: 'var(--text-body)',
                  fontWeight: 500,
                }}
              >
                {item.q}
              </summary>
              <p
                style={{
                  margin: 'var(--space-3) 0 0 0',
                  fontSize: 'var(--text-meta)',
                  color: 'var(--color-on-surface-muted)',
                  lineHeight: 1.5,
                }}
              >
                {item.a}
              </p>
            </details>
          ))}
        </div>
      </RevealBlock>
    </section>
  );
}

function BottomCta() {
  const { t } = useI18n();
  const trialDays = useTrialDays();
  return (
    <section
      style={{
        padding: 'var(--space-12) var(--space-6) var(--space-16) var(--space-6)',
        textAlign: 'center',
      }}
    >
      <Link
        to="/signup"
        style={{
          display: 'inline-block',
          padding: '14px 32px',
          borderRadius: 'var(--radius-pill)',
          background: 'var(--color-primary)',
          color: 'var(--color-primary-fg)',
          fontWeight: 600,
          textDecoration: 'none',
          boxShadow: 'var(--shadow-2)',
        }}
      >
        {t('marketing.pricing.bottomCta', { days: trialDays })}
      </Link>
    </section>
  );
}

const eyebrowStyle: React.CSSProperties = {
  fontSize: 'var(--text-meta)',
  color: 'var(--color-primary)',
  letterSpacing: 3,
  textTransform: 'uppercase',
  fontWeight: 600,
  margin: 0,
};
